import React, {
  useState,
  useMemo,
  useCallback,
  memo,
  useRef,
  useEffect,
} from 'react'
import { makeStyles } from '@material-ui/core/styles'
import { Button, FormControl } from '@material-ui/core'
import { styles } from './styles'
import {
  FILTERS_OPERATORS,
  FILTERS_OPERATORS_LABELS,
  CUSTOM_OPERATOR,
} from './options'
import { OperatorSelect, ValueInput } from './components'

const useStyles = makeStyles(() => styles)

const initialState = {
  operator: FILTERS_OPERATORS.ALL,
  value1: '',
  value2: '',
  showOperatorSelect: true,
  operators: FILTERS_OPERATORS,
  operatorLabels: FILTERS_OPERATORS_LABELS,
  isUpdating: false,
  previousOperator: FILTERS_OPERATORS.ALL,
  previousValue1: '',
  previousValue2: '',
  previousOperators: FILTERS_OPERATORS,
  previousOperatorLabels: FILTERS_OPERATORS_LABELS,
}

export const ExtendedSelect = ({ label, onUpdate, toInitValue = false }) => {
  const classes = useStyles()
  const expandedContentRef = useRef(null)
  const selectRef = useRef(null)

  const [state, setState] = useState(initialState)

  useEffect(() => {
    if (toInitValue) {
      setState(initialState)
    }
  }, [toInitValue])

  useEffect(() => {
    const handleClickOutside = event => {
      // Don't handle clicks if we're interacting with the menu
      if (event.target.closest('.MuiMenu-paper')) {
        return
      }

      // Check if click is on any select or within expanded content
      const clickedSelect = event.target.closest('.MuiSelect-root')
      const clickedInput = event.target.closest('.MuiInput-root')
      const isClickInCurrentComponent =
        expandedContentRef.current &&
        expandedContentRef.current.contains(event.target)

      // If clicking within current expanded component (including inputs), allow it
      if (isClickInCurrentComponent) {
        return
      }

      // If clicking on another select while expanded, reset
      if (
        clickedSelect &&
        !isClickInCurrentComponent &&
        !state.showOperatorSelect
      ) {
        setState(prev => ({
          ...prev,
          showOperatorSelect: true,
          operator: prev.previousOperator,
          value1: prev.previousValue1,
          value2: prev.previousValue2,
          operators: prev.previousOperators,
          operatorLabels: prev.previousOperatorLabels,
        }))
        return
      }

      // If filter is expanded and click is completely outside
      if (
        !state.showOperatorSelect &&
        expandedContentRef.current &&
        !clickedInput
      ) {
        setState(prev => ({
          ...prev,
          showOperatorSelect: true,
          operator: prev.previousOperator,
          value1: prev.previousValue1,
          value2: prev.previousValue2,
          operators: prev.previousOperators,
          operatorLabels: prev.previousOperatorLabels,
        }))
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [state.showOperatorSelect])

  const handleOperatorChange = useCallback(event => {
    const newOperator = event.target.value
    setState(prev => {
      // If changing from All to another operator
      if (prev.operator === FILTERS_OPERATORS.ALL) {
        return {
          ...prev,
          operator: newOperator,
          value1: '',
          value2: '',
          showOperatorSelect: false,
          operators: FILTERS_OPERATORS,
        }
      }

      // If changing between non-All operators
      return {
        ...prev,
        operator: newOperator,
        value1: '',
        value2: '',
        showOperatorSelect: false,
      }
    })
  }, [])

  const handleValueChange = useCallback(
    field => value => {
      setState(prev => ({
        ...prev,
        [field]: value,
      }))
    },
    [],
  )

  const handleUpdate = useCallback(async () => {
    try {
      setState(prev => ({
        ...prev,
        isUpdating: true,
      }))

      if (state.operator === FILTERS_OPERATORS.ALL) {
        await onUpdate(null)
      } else {
        await onUpdate({
          operator: state.operator,
          value1: Number(state.value1),
          value2: Number(state.value2),
        })
      }

      setState(prev => {
        if (prev.operator === FILTERS_OPERATORS.ALL) {
          return {
            ...prev,
            showOperatorSelect: true,
            operators: FILTERS_OPERATORS,
            operatorLabels: FILTERS_OPERATORS_LABELS,
            value1: '',
            value2: '',
            isUpdating: false,
            previousOperator: FILTERS_OPERATORS.ALL,
            previousValue1: '',
            previousValue2: '',
            previousOperators: FILTERS_OPERATORS,
            previousOperatorLabels: FILTERS_OPERATORS_LABELS,
          }
        }

        const customLabel =
          prev.operator === FILTERS_OPERATORS.BETWEEN
            ? `≥ ${prev.value1} and ≤ ${prev.value2}`
            : `${
                prev.operator === FILTERS_OPERATORS.GREATER_EQUAL ? '≥' : '≤'
              } ${prev.value1}`

        return {
          ...prev,
          operator: CUSTOM_OPERATOR,
          previousOperator: CUSTOM_OPERATOR,
          previousValue1: prev.value1,
          previousValue2: prev.value2,

          showOperatorSelect: true,
          operators: {
            ...FILTERS_OPERATORS,
            [CUSTOM_OPERATOR]: CUSTOM_OPERATOR,
          },
          operatorLabels: {
            ...FILTERS_OPERATORS_LABELS,
            [CUSTOM_OPERATOR]: customLabel,
          },
          previousOperators: {
            ...FILTERS_OPERATORS,
            [CUSTOM_OPERATOR]: CUSTOM_OPERATOR,
          },
          previousOperatorLabels: {
            ...FILTERS_OPERATORS_LABELS,
            [CUSTOM_OPERATOR]: customLabel,
          },
          isUpdating: false,
        }
      })
    } catch (error) {
      console.error('Error updating values:', error)
      setState(prev => ({
        ...prev,
        isUpdating: false,
      }))
    }
  }, [state.operator, state.value1, state.value2, onUpdate])

  const buttonDisabled = useMemo(
    () =>
      (state.operator === FILTERS_OPERATORS.BETWEEN &&
        (!state.value1 || !state.value2)) ||
      ([FILTERS_OPERATORS.GREATER_EQUAL, FILTERS_OPERATORS.LESS_EQUAL].includes(
        state.operator,
      ) &&
        !state.value1),
    [state.operator, state.value1, state.value2],
  )

  return (
    <div className={classes.wrapper}>
      <span
        style={{ marginRight: state.showOperatorSelect ? '10px' : '174px' }}
        className={classes.label}
      >
        {label}
      </span>
      <div className={classes.selectContainer}>
        {state.showOperatorSelect ? (
          <FormControl className={classes.select} style={{ width: '164px' }}>
            <OperatorSelect
              ref={selectRef}
              operator={state.operator}
              operators={state.operators}
              operatorLabels={state.operatorLabels}
              onChange={handleOperatorChange}
              className={classes.menuItem}
            />
          </FormControl>
        ) : (
          <div
            className={classes.expandedContent}
            ref={expandedContentRef}
            style={{ width: '244px' }}
          >
            <FormControl
              className={classes.select}
              style={{ marginBottom: '15px' }}
            >
              <OperatorSelect
                ref={selectRef}
                operator={state.operator}
                operators={state.operators}
                operatorLabels={state.operatorLabels}
                onChange={handleOperatorChange}
                className={classes.menuItem}
              />
            </FormControl>

            {state.operator === FILTERS_OPERATORS.BETWEEN ? (
              <div className={classes.betweenInputs}>
                <ValueInput
                  value={state.value1}
                  onChange={handleValueChange('value1')}
                />
                <span>and</span>
                <ValueInput
                  value={state.value2}
                  onChange={handleValueChange('value2')}
                />
              </div>
            ) : null}
            {[
              FILTERS_OPERATORS.GREATER_EQUAL,
              FILTERS_OPERATORS.LESS_EQUAL,
            ].includes(state.operator) ? (
              <ValueInput
                value={state.value1}
                onChange={handleValueChange('value1')}
                fullWidth
                className={classes.input}
              />
            ) : null}

            <div className={classes.buttonContainer}>
              <Button
                variant="contained"
                fullWidth
                className={classes.updateButton}
                onClick={handleUpdate}
                disabled={buttonDisabled || state.isUpdating}
              >
                {state.isUpdating ? 'UPDATING...' : 'UPDATE'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}

export default memo(ExtendedSelect)
